import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faNewspaper, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import usePublicationBooking from '@hooks/usePublicationBooking'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useManagePlannerBooking from './hooks/useManagePlannerBooking'

import PublicationOption from './components/PublicationOption'

const MODAL_KEY = 'ManagePlannerBookingModal'

type ModalCallbacks = {
  closeModal: () => void,
  showSelectLayoutTemplateModal: VoidFunction,
  updatePublicationBooking: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManagePlannerBookingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal,
    showSelectLayoutTemplateModal,
    updatePublicationBooking,
  } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { publicationBooking: initPublicationBooking } = modalPayload

  const managePlannerBookingPayload = useManagePlannerBooking(initPublicationBooking)
  const {
    callbacks: {
      updatePublicationBookingPageOption,
      deletePublicationBooking: deleteFn,
    },
    notesEnabled,
    pagePositionEnabled,
    publicationBookingPayload: {
      callbacks: {
        updatePublicationBooking: updateFn,
      },
      creating,
      publicationBooking,
      updating,
      urls: {
        downloadLowResPDFUrl,
        downloadPDFUrl,
      },
    },
    publicationBookingFormPayload: {
      entityState,
      entityState: {
        booking_confirmation_id,
        page_position,
        notes,
      },
      handlers,
      saveEnabled,
    },
    publicationOptionsPayload: {
      filteredPublicationOptions,
    },
    selectedPageOptions,
  } = managePlannerBookingPayload

  // To be used with handleCallbackAction
  const updatePublicationBookingPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Publication Booking Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      deleteFn,
      afterAction: closeModal,
    },
    strings: {
      confirmString: 'Are you sure? This will delete the page.',
      deleteString: 'Delete',
      entityName: 'Booking Page',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Page" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Booking Confirmation ID">
            <input name="booking_confirmation_id" type="text" value={booking_confirmation_id} {...handlers} />
          </Form.Field>

          {notesEnabled && (
            <Form.Field label="Notes" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <textarea
                name="notes"
                placeholder="Special requests are not guaranteed."
                value={notes}
                style={{ height: 80 }}
                {...handlers}
              />
            </Form.Field>
          )}

          {filteredPublicationOptions.map(publicationOption => (
            <PublicationOption
              callbacks={{
                updatePublicationBookingPageOption: value => (
                  // @ts-expect-error TS2345
                  updatePublicationBookingPageOption(value, publicationOption)
                ),
              }}
              // @ts-expect-error TS7006
              currentValue={selectedPageOptions.find(pageOption => pageOption.id === publicationOption.id)?.value || ''}
              // @ts-expect-error TS18046
              key={publicationOption.id}
              // @ts-expect-error TS2322
              publicationOption={publicationOption}
            />
          ))}

          {/* Page Position */}
          {publicationBooking.id && pagePositionEnabled && (
            <Form.Field label="Page Position" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select value={page_position} name="page_position" {...handlers}>
                <option value="">Please Select...</option>
                <option value="LHS">LHS</option>
                <option value="RHS">RHS</option>
              </select>
            </Form.Field>
          )}
          {/*  End Page Position */}

          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faNewspaper} />}
              onClick={() => showSelectLayoutTemplateModal()}
              size="medium"
            >
              Change Layout
            </Button>

            <AuthenticatedDownload>
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryUtility"
                href={downloadLowResPDFUrl}
                icon={<FontAwesomeIcon icon={faDownload} />}
                marginTop="medium"
                size="medium"
              >
                Download Web Quality
              </Button>
            </AuthenticatedDownload>

            <AuthenticatedDownload>
              <Button
                // @ts-expect-error TS2322
                as="a"
                buttonStyle="secondaryUtility"
                href={downloadPDFUrl}
                icon={<FontAwesomeIcon icon={faDownload} />}
                marginTop="medium"
                size="medium"
              >
                Download Print Quality
              </Button>
            </AuthenticatedDownload>

            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              marginTop="medium"
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmDeletePayload)}
              size="medium"
            >
              Delete Booking Page
            </Button>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          // @ts-expect-error TS2345
          onClick={() => updatePublicationBooking(updatePublicationBookingPayload)}
          size="large"
        >
          {saveEnabled ? 'Save Booking' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManagePlannerBookingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
